import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import api from "../services/api";
import * as XLSX from "xlsx";
import "../styles/dashboard.css";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

export default function Dashboard() {
  // --- Dashboard stats ---
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    stateCount: 0,
    districtCount: 0,
    blockCount: 0,
    gpCount: 0
  });

  // --- User modal (list users by role) ---
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState([]);
  const [loading, setLoading] = useState(false);

  // --- Edit member modal ---
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
 const [editForm, setEditForm] = useState({
  name: "", email: "", phone: "", employeeId: "", level: "", role: "",
  stateId: "", districtId: "", blockId: "", gpId: "", village: "",
  postOffice: "", pin: "", aadhaar: "", pan: "", bankName: "",
  accountNumber: "", ifsc: "", branch: "",
  // Add image fields
  aadhaarBase64: "",
  panBase64: "",
  passbookBase64: "",
  photoBase64: ""
});

const [editPreviews, setEditPreviews] = useState({
  aadhaar: null,
  pan: null,
  passbook: null,
  photo: null
});
  // --- Revenue state ---
  const [districtRevenue, setDistrictRevenue] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [revenueError, setRevenueError] = useState(false);
  const [overallTotals, setOverallTotals] = useState({
    totalRevenue: 0,
    totalCount: 0
  });

  // --- Load data on mount ---
  useEffect(() => {
    loadDashboardData();
    loadRevenueData();
  }, []);

  // --- Fetch dashboard stats (total users, coordinators count) ---
  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard");
      setDashboardData(res.data);
    } catch (err) {
      console.error("Error loading dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  // --- Fetch revenue data (district + monthly) and calculate totals ---
  const loadRevenueData = async () => {
    setRevenueLoading(true);
    setRevenueError(false);
    try {
      const [districtRes, monthlyRes] = await Promise.all([
        api.get("/dashboard/revenue/district"),
        api.get("/dashboard/revenue/monthly")
      ]);
      setDistrictRevenue(districtRes.data);
      setMonthlyRevenue(monthlyRes.data);

      // Calculate overall totals from district data (using paidRevenue as total revenue)
      let totalRevenue = 0, totalCount = 0;
      districtRes.data.forEach(dist => {
        totalRevenue += dist.paidRevenue || 0;
        totalCount += dist.paidCount || 0;
      });
      setOverallTotals({ totalRevenue, totalCount });
    } catch (err) {
      console.error("Revenue load error:", err);
      setRevenueError(true);
    } finally {
      setRevenueLoading(false);
    }
  };

  // Prepare data for monthly bar chart
  const getMonthlyChartData = () => {
    if (!monthlyRevenue.length) return [];
    return monthlyRevenue.map(item => ({
      month: new Date(item.year, item.month - 1).toLocaleString('default', { month: 'short', year: 'numeric' }),
      revenue: item.totalRevenue,
      count: item.paymentCount
    }));
  };

  // --- Export to Excel (only District, Total Revenue, Total Count) ---
  const exportToExcel = () => {
    if (districtRevenue.length === 0) return;

    const excelData = districtRevenue.map((item) => ({
      "District": item.districtName,
      "Total Revenue (₹)": item.paidRevenue || 0,
      "Total Count": item.paidCount || 0
    }));

    // Add overall totals row
    excelData.push({
      "District": "📊 OVERALL TOTAL",
      "Total Revenue (₹)": overallTotals.totalRevenue,
      "Total Count": overallTotals.totalCount
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "District Revenue");
    XLSX.writeFile(wb, `district_revenue_${new Date().toISOString().slice(0, 19)}.xlsx`);
  };

  // --- Load users by role (for stat cards click) ---
  const loadUsersByRole = async (role, title) => {
    setLoading(true);
    setModalTitle(title);
    try {
      const res = await api.get(`/dashboard/users/by-role/${role}`);
      setModalData(res.data);
      setShowModal(true);
    } catch (err) {
      console.error("Error loading users:", err);
      alert("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // --- Load a single member for editing ---
  const loadMemberForEdit = async (member) => {
    if (!member.id) {
      alert("Cannot edit: Member ID not found");
      return;
    }
    setEditLoading(true);
    try {
      const res = await api.get(`/dashboard/${member.id}`);
      setEditMember(res.data);
      setEditForm({
        name: res.data.name || "", email: res.data.email || "",
        phone: res.data.phone || "", employeeId: res.data.employeeId || "",
        level: res.data.level || "", role: res.data.role || "",
        stateId: res.data.stateId || "", districtId: res.data.districtId || "",
        blockId: res.data.blockId || "", gpId: res.data.gpId || "",
        village: res.data.village || "", postOffice: res.data.postOffice || "",
        pin: res.data.pin || "", aadhaar: res.data.aadhaar || "",
        pan: res.data.pan || "", bankName: res.data.bankName || "",
        accountNumber: res.data.accountNumber || "", ifsc: res.data.ifsc || "",
        branch: res.data.branch || ""
      });
      setShowEditModal(true);
    } catch (err) {
      alert("Failed to load member details");
    } finally {
      setEditLoading(false);
    }
  };

  // --- Save edited member ---
  const saveEditedMember = async () => {
    if (!editMember?.id) return;
    setEditLoading(true);
    try {
      await api.put(`/dashboard/${editMember.id}`, editForm);
      alert("✅ Member updated");
      setShowEditModal(false);
      // Refresh the current user list modal if open
      if (showModal) {
        const roleMap = {
          "State Coordinators": "STATE_COORDINATOR",
          "District Coordinators": "DISTRICT_COORDINATOR",
          "Block Coordinators": "BLOCK_COORDINATOR",
          "GP Coordinators": "GP_COORDINATOR"
        };
        const role = roleMap[modalTitle];
        if (role) {
          const res = await api.get(`/dashboard/users/by-role/${role}`);
          setModalData(res.data);
        }
      }
      loadDashboardData(); // refresh stats
    } catch (err) {
      alert("❌ Update failed");
    } finally {
      setEditLoading(false);
    }
  };
  const deleteMember = async (member) => {
  if (!member?.id) {
    alert("Cannot delete: Member ID not found");
    return;
  }

  const confirmed = window.confirm(
    `Are you sure you want to delete ${member.name}?`
  );

  if (!confirmed) return;

  try {
    await api.delete(`/users/${member.id}`);

    alert("User deleted successfully");

    // Remove deleted user from the current modal
    setModalData((prev) =>
      prev.filter((item) => item.id !== member.id)
    );

    // Refresh dashboard counts
    await loadDashboardData();
  } catch (err) {
    console.error("Delete user error:", err);
    alert(
      err.response?.data?.message ||
      "Failed to delete user. Please try again."
    );
  }
};

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const closeModal = () => {
    setShowModal(false);
    setModalData([]);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditMember(null);
  };

  // Format month for monthly revenue table
  const formatMonth = (year, month) => {
    const date = new Date(year, month - 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="dashboard-content">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-title">
            <h1>Dashboard</h1>
            <p>Welcome back, Admin</p>
          </div>
          <div className="header-date">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
            })}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-container">
          <StatCard title="Total Users" value={dashboardData.totalUsers} icon="👥" color="#4361ee" bgColor="#e0e7ff" />
          <StatCard title="State Coordinators" value={dashboardData.stateCount} icon="🏛️" color="#3b82f6" bgColor="#dbeafe" onClick={() => loadUsersByRole("STATE_COORDINATOR", "State Coordinators")} />
          <StatCard title="District Coordinators" value={dashboardData.districtCount} icon="📊" color="#10b981" bgColor="#d1fae5" onClick={() => loadUsersByRole("DISTRICT_COORDINATOR", "District Coordinators")} />
          <StatCard title="Block Coordinators" value={dashboardData.blockCount} icon="📍" color="#f59e0b" bgColor="#fed7aa" onClick={() => loadUsersByRole("BLOCK_COORDINATOR", "Block Coordinators")} />
          <StatCard title="GP Coordinators" value={dashboardData.gpCount} icon="🏘️" color="#ef4444" bgColor="#fee2e2" onClick={() => loadUsersByRole("GP_COORDINATOR", "GP Coordinators")} />
        </div>

        {/* Revenue Section */}
        <div className="revenue-container">
          {/* Overall Totals Summary (only Total Revenue and Total Payments) */}
          {!revenueLoading && !revenueError && (
            <div className="totals-summary compact">
              <div className="compact-totals">
                <span className="totals-label">💰 Total Revenue:</span>
                <span className="totals-value">₹ {Number(overallTotals.totalRevenue).toLocaleString('en-IN')}</span>
                <span className="totals-label">📦 Total CARDS:</span>
                <span className="totals-value">{overallTotals.totalCount}</span>
              </div>
            </div>
          )}

          {/* District-wise Revenue Table with Export Button */}
          <div className="revenue-card">
            <div className="section-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2>📊 District-wise Revenue</h2>
                <p>Total collection per district (PAID payments only)</p>
              </div>
              <button className="export-excel-btn" onClick={exportToExcel} disabled={districtRevenue.length === 0}>
                📎 Export to Excel
              </button>
            </div>
            {revenueLoading ? (
              <div className="loading-spinner">Loading revenue data...</div>
            ) : revenueError ? (
              <div className="error-message">⚠️ Failed to load revenue data. Please try again later.</div>
            ) : districtRevenue.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>District</th>
                    <th>Total Revenue (₹)</th>
                    <th>Total Cards</th>
                  </tr>
                </thead>
                <tbody>
                  {districtRevenue.map((item, idx) => {
                    const totalRevenue = item.paidRevenue || 0;
                    const totalCount = item.paidCount || 0;
                    return (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{item.districtName}</td>
                        <td className="amount-cell">₹ {totalRevenue.toLocaleString('en-IN')}</td>
                        <td>{totalCount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="no-data">No revenue data available</div>
            )}
          </div>

          {/* Monthly-wise Revenue with Bar Chart (unchanged) */}
          <div className="revenue-card">
            <div className="section-header">
              <h2>📅 Monthly-wise Revenue</h2>
              <p>Total collection per month (PAID payments)</p>
            </div>
            {revenueLoading ? (
              <div className="loading-spinner">Loading revenue data...</div>
            ) : revenueError ? (
              <div className="error-message">⚠️ Failed to load revenue data.</div>
            ) : monthlyRevenue.length > 0 ? (
              <>
                <div className="monthly-chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getMonthlyChartData()} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => `₹${value / 1000}k`} />
                      <Tooltip formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Revenue']} />
                      <Legend />
                      <Bar dataKey="revenue" fill="#3b82f6" name="Revenue (₹)" radius={[8,8,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <table className="data-table">
                  <thead>
                    <tr><th>#</th><th>Month</th><th>Total Revenue (₹)</th><th>Payments Count</th></tr>
                  </thead>
                  <tbody>
                    {monthlyRevenue.map((item, idx) => (
                      <tr key={idx}>
                        <td>{idx+1}</td>
                        <td>{formatMonth(item.year, item.month)}</td>
                        <td className="amount-cell">₹ {Number(item.totalRevenue).toLocaleString('en-IN')}</td>
                        <td>{item.paymentCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <div className="no-data">No monthly revenue data available</div>
            )}
          </div>
        </div>

        {/* User List Modal (for role clicks) – unchanged */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{modalTitle}</h3>
                <button className="modal-close" onClick={closeModal}>×</button>
              </div>
              <div className="modal-body">
                {loading ? (
                  <div className="loading-spinner">Loading...</div>
                ) : modalData.length > 0 ? (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Phone</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalData.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.name}</td>
                          <td>{item.email}</td>
                          <td><span className="role-badge">{item.role?.replace(/_/g, ' ')}</span></td>
                          <td>{item.phone || '-'}</td>
                         <td style={{ display: "flex", gap: "8px" }}>
  <button
    className="edit-member-btn"
    onClick={() => loadMemberForEdit(item)}
  >
    ✏️ Edit
  </button>

  <button
    className="delete-member-btn"
    onClick={() => deleteMember(item)}
  >
    🗑️ Delete
  </button>
</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data">No records found</div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={closeModal}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Member Modal (Full Form) – unchanged */}
        {showEditModal && editMember && (
          <div className="modal-overlay" onClick={closeEditModal}>
            <div className="modal-container edit-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>✏️ Edit Member: {editMember.name}</h3>
                <button className="modal-close" onClick={closeEditModal}>×</button>
              </div>
              <div className="modal-body edit-modal-body">
                {editLoading ? (
                  <div className="loading-spinner">Loading...</div>
                ) : (
                  <form className="edit-form">
                    {/* Basic Information */}
                    <div className="form-section">
                      <h4>📋 Basic Information</h4>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Full Name *</label>
                          <input type="text" name="name" value={editForm.name} onChange={handleEditChange} required />
                        </div>
                        <div className="form-group">
                          <label>Email *</label>
                          <input type="email" name="email" value={editForm.email} onChange={handleEditChange} required />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Role *</label>
                          <select name="role" value={editForm.role} onChange={handleEditChange} required>
                            <option value="">Select Role</option>
                            <option value="STATE_COORDINATOR">🏛️ State Coordinator</option>
                            <option value="DISTRICT_COORDINATOR">📊 District Coordinator</option>
                            <option value="BLOCK_COORDINATOR">📍 Block Coordinator</option>
                            <option value="GP_COORDINATOR">🏘️ GP Coordinator</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Phone</label>
                          <input type="tel" name="phone" value={editForm.phone} onChange={handleEditChange} />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Employee ID</label>
                          <input type="text" name="employeeId" value={editForm.employeeId} onChange={handleEditChange} />
                        </div>
                        <div className="form-group">
                          <label>Level</label>
                          <input type="text" name="level" value={editForm.level} onChange={handleEditChange} placeholder="e.g., Senior, Junior" />
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div className="form-section">
                      <h4>📍 Address Information</h4>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Village/Town</label>
                          <input type="text" name="village" value={editForm.village} onChange={handleEditChange} />
                        </div>
                        <div className="form-group">
                          <label>Post Office</label>
                          <input type="text" name="postOffice" value={editForm.postOffice} onChange={handleEditChange} />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>PIN Code</label>
                          <input type="text" name="pin" value={editForm.pin} onChange={handleEditChange} />
                        </div>
                        <div className="form-group">
                          <label>Aadhaar Number</label>
                          <input type="text" name="aadhaar" value={editForm.aadhaar} onChange={handleEditChange} />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>PAN Number</label>
                          <input type="text" name="pan" value={editForm.pan} onChange={handleEditChange} />
                        </div>
                      </div>
                    </div>

                    {/* Bank Details */}
                    <div className="form-section">
                      <h4>🏦 Bank Details</h4>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Bank Name</label>
                          <input type="text" name="bankName" value={editForm.bankName} onChange={handleEditChange} />
                        </div>
                        <div className="form-group">
                          <label>Account Number</label>
                          <input type="text" name="accountNumber" value={editForm.accountNumber} onChange={handleEditChange} />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>IFSC Code</label>
                          <input type="text" name="ifsc" value={editForm.ifsc} onChange={handleEditChange} />
                        </div>
                        <div className="form-group">
                          <label>Branch Name</label>
                          <input type="text" name="branch" value={editForm.branch} onChange={handleEditChange} />
                        </div>
                      </div>
                    </div>
                  </form>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={closeEditModal}>Cancel</button>
                <button className="btn-save" onClick={saveEditedMember} disabled={editLoading}>
                  {editLoading ? "💾 Saving..." : "💾 Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, icon, color, bgColor, onClick }) {
  return (
    <div className={`stat-card ${onClick ? 'clickable' : ''}`} onClick={onClick}>
      <div className="stat-card-icon" style={{ backgroundColor: bgColor, color: color }}>
        {icon}
      </div>
      <div className="stat-card-info">
        <h4>{title}</h4>
        <p>{value}</p>
      </div>
    </div>
  );
}